import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Update } from '@ngrx/entity';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { SubTypeCategoryUpdated, SubTypeCategoryOnServerCreated } from 'src/app/core/linked-tables/_actions/sub-type-category.actions';
import { SubTypeCategoryModel } from 'src/app/core/linked-tables/_models/sub-type-category.model';
import { selectSubTypeCategorysActionLoading, selectSubTypeCategoryById, selectLastCreatedSubTypeCategoryId } from 'src/app/core/linked-tables/_selectors/sub-type-category.selectors';
import { AppState } from 'src/app/core/reducers';
import {Location} from '@angular/common';
import { SubheaderService } from 'src/app/core/_base/layout';
import { AllAssetClassTypesRequested, AssetClassType, selectAllAssetClassTypes } from 'src/app/core/linked-tables';
import {combineLatest} from 'rxjs'

@Component({
  selector: 'kt-sub-type-category-edit',
  templateUrl: './sub-type-category-edit.component.html',
  styleUrls: ['./sub-type-category-edit.component.scss']
})
export class SubTypeCategoryEditComponent implements OnInit {
    model: SubTypeCategoryModel;
    oldModel: SubTypeCategoryModel;

    subTypeCategoryForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    readonly = false
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    propertySubTypes: AssetClassType[] = [];

    /**
     * Component constructor
     *
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location
     * @param subheaderService: SubheaderService
     * @param store: Store<AppState>
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                public subheaderService: SubheaderService,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectSubTypeCategorysActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectSubTypeCategoryById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initSubTypeCategory();
                    }
                });
            } else {
                this.model = new SubTypeCategoryModel();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initSubTypeCategory();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }

        this.store.dispatch(new AllAssetClassTypesRequested())
        this.store.select(selectAllAssetClassTypes).subscribe(res => {
          this.propertySubTypes = [];
          if (res) {
            this.propertySubTypes = res;
          }
        })
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Bases of Values', page: `../default/admin-management/linked-tables/base-of-values`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../default/admin-management/linked-tables/base-of-values/edit/${id}`
                    : `../default/admin-management/linked-tables/base-of-values/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initSubTypeCategory() {
        this.createForm();
        // set subheaderService
    }


    createForm() {
        this.subTypeCategoryForm = this.fb.group({
            name: [this.model.name, Validators.required],
            property_sub_type_id: [this.model.property_sub_type_id, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('SUB_TYPE_CATEGORY.FORM.TITLE.EDIT', {name: this.model.name});
        }

        return this.translate.instant('SUB_TYPE_CATEGORY.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.subTypeCategoryForm.controls[controlName];
        const result = control.invalid && control.touched;
        return result;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.subTypeCategoryForm.markAsPristine();
        this.subTypeCategoryForm.markAsUntouched();
        this.subTypeCategoryForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.subTypeCategoryForm.controls;
        /** check form */
        if (this.subTypeCategoryForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }
        this.model.property_sub_type_id = this.subTypeCategoryForm.controls.property_sub_type_id.value;

        if (this.model.id > 0) {
            this.updateSubTypeCategory(this.model);
        } else {
            this.createSubTypeCategory(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: SubTypeCategoryModel
     */
    updateSubTypeCategory(_model: SubTypeCategoryModel) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateSubTypeCategory: Update<SubTypeCategoryModel> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new SubTypeCategoryUpdated({
            partialItem: updateSubTypeCategory,
            item: _model
        }));
        this.oldModel = _model;

        this.navigateToParent();

    }

    /**
     * Create model
     *
     * @param _model: SubTypeCategoryModel
     */
    createSubTypeCategory(_model: SubTypeCategoryModel) {
        this.store.dispatch(new SubTypeCategoryOnServerCreated({item: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedSubTypeCategoryId),
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0 && this.subTypeCategoryForm.controls.property_sub_type_id.value);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }


    canDeactivate() {
        if (this.discard()) {
            if (window.confirm(this.translate.instant('GENERAL.MESSAGE.LOST_CHANGES'))) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name && this.model.property_sub_type_id != this.oldModel.property_sub_type_id;
        }
        return false;
    }
}
